import React from "react";
import styled from "@emotion/styled";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { theme } from "../../nessie-web";
import { useTeacherSignupMobileRedesign } from "@src/hooks/useTeacherSignupMobileRedesign";
import isMobile from "@src/utils/isMobile";
import { CommonModalProps } from "../CommonModal";
import CommonModal from "../CommonModal";
import { Flex } from "../../Boxes";
import { Text } from "../../Text";
import Translate from "../../translation/Translate";
import { logEvent as le } from "@src/utils/logClient";

const {
  colors: { contentAccent },
} = theme;

// Shared styled components
export const Logo = styled.img`
  height: 60px;
  margin-top: 12px;
`;

export const LinkButton = styled("button")`
  font-family: Ginto, sans-serif;
  border: 0;
  background: transparent;
  font-weight: bold;
  color: ${contentAccent};
  &:hover {
    text-decoration: underline;
  }
  cursor: pointer;
`;

export const ModalTitle = styled("h3")`
  font-size: 36px;
  font-weight: 800;
  margin: 18px 0;
  line-height: 40px;
  text-align: center;
`;

export const ModalTitleMobile = styled("h3")`
  font-family: Ginto, sans-serif;
  font-weight: 800;
  font-size: 23px;
  margin: ${theme.space.s}px 0;
  margin-bottom: 5px;
  text-align: center;
`;

// Helper function to get common modal props based on mobile redesign
export const getMobileRedesignModalProps = (
  isMobileOptimized: boolean,
  additionalProps: Partial<CommonModalProps> = {},
): Partial<CommonModalProps> => ({
  fullPage: isMobileOptimized,
  fullScreen: !isMobileOptimized && isMobile().any,
  useMobileRedesign: isMobileOptimized,
  ...additionalProps,
});

// Reusable component for logo/badge conditional rendering
interface LogoBadgeProps {
  isMobileOptimized: boolean;
  badge?: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
  badgeAlt?: string;
  logoSrc?: string;
  logoAlt?: string;
  badgeStyles?: React.CSSProperties;
}

export const LogoBadge: React.FC<LogoBadgeProps> = ({
  isMobileOptimized,
  badge,
  badgeAlt = "",
  logoSrc = "https://static.classdojo.com/img/logo_color_2024.png",
  logoAlt = "",
  badgeStyles,
}) => {
  return isMobileOptimized ? (
    <Logo src={logoSrc} alt={logoAlt} />
  ) : badge ? (
    <GatsbyImage image={badge.childImageSharp.gatsbyImageData} loading="eager" alt={badgeAlt} style={badgeStyles} />
  ) : null;
};

// Function to get the appropriate title component
export const getTitleComponent = (isMobileOptimized: boolean) => {
  return isMobileOptimized ? ModalTitleMobile : ModalTitle;
};

// Function to get mobile redesign text styles
export const getMobileRedesignTextProps = (isMobileOptimized: boolean) => {
  return {
    fontFamily: isMobileOptimized ? "Ginto" : "",
  };
};

// Higher-order component for signup modals with mobile redesign
interface SignupModalWrapperProps {
  badge?: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
  badgeAlt?: string;
  badgeStyles?: React.CSSProperties;
  titlePath: string;
  subtitlePath: string;
  loginEventName: string;
  showLoginModal: () => void;
  children: React.ReactNode;
  modalProps?: Partial<CommonModalProps>;
}

export const SignupModalWrapper: React.FC<SignupModalWrapperProps> = ({
  badge,
  badgeAlt = "",
  badgeStyles,
  titlePath,
  subtitlePath,
  loginEventName,
  showLoginModal,
  children,
  modalProps = {},
}) => {
  const isMobileOptimized = useTeacherSignupMobileRedesign();
  const ModalTitleComponent = getTitleComponent(isMobileOptimized);
  const textProps = getMobileRedesignTextProps(isMobileOptimized);

  return (
    <CommonModal noHeader {...getMobileRedesignModalProps(isMobileOptimized)} {...modalProps}>
      <LogoBadge isMobileOptimized={isMobileOptimized} badge={badge} badgeAlt={badgeAlt} badgeStyles={badgeStyles} />
      <Flex flexDirection="column" alignItems="center" maxWidth={["100%", "85%"]} mx="auto">
        <ModalTitleComponent as="h2">
          <Translate path={titlePath} />
        </ModalTitleComponent>
        {children}
        <div>
          <Text display="inline-block" {...textProps}>
            <Translate path={subtitlePath} />
          </Text>
          <LinkButton
            onClick={() => {
              le({
                eventName: loginEventName,
                eventValue: "domain_email_signup_overlay_variant",
              });
              showLoginModal();
            }}
          >
            <Translate path="components.signup_teacher.log_in_here" />
          </LinkButton>
        </div>
      </Flex>
    </CommonModal>
  );
};
